import { Anchor } from 'src/design/atoms/Anchor';
import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Center } from 'src/design/atoms/Center';
import { Group } from 'src/design/atoms/Group';
import { List } from 'src/design/atoms/List';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BasicButton } from 'src/design/atoms/BasicButton';
import React from 'react';
import {
  useDeclineSmsConsent,
  useRequestPhoneVerification,
  useVerifyPhone,
} from 'src/api/queries';
import { useAuth } from 'src/hooks/useAuth';
import { PhoneVerificationStepper } from 'src/app/notifications/PhoneVerificationStepper';

interface SmsConsentProps {
  /** Refreshes the cached user (clears requiresSmsConsentPrompt) so the app renders. */
  onComplete: () => void;
}

/**
 * Full-screen, first-login consent interstitial rendered by App.tsx in place
 * of the app shell whenever `user.requiresSmsConsentPrompt` is true.
 * Deliberately outside AppShell — this is shown before the user has any
 * reason to see navigation.
 */
export const SmsConsent: React.FC<SmsConsentProps> = ({ onComplete }) => {
  const { loadUserFromCache } = useAuth();
  const [step, setStep] = React.useState<'consent' | 'verify'>('consent');
  const declineMutation = useDeclineSmsConsent();
  const requestCodeMutation = useRequestPhoneVerification();
  const verifyMutation = useVerifyPhone();

  const handleDecline = async () => {
    await declineMutation.mutateAsync();
    await loadUserFromCache();
    onComplete();
  };

  const handleVerified = async () => {
    await loadUserFromCache();
    onComplete();
  };

  return (
    <Box style={{ minHeight: '100vh' }} bg="gray.0">
      <Center style={{ minHeight: '100vh' }}>
        <Card p="xl" maw={480} miw={340} withBorder>
          {step === 'consent' ? (
            <Stack>
              <Title order={3}>Get text alerts for your timesheets</Title>
              <List spacing="xs" size="sm">
                <List.Item>
                  <b>What we send:</b> timesheet workflow notifications only —
                  submissions, approvals, and rejections. No marketing, no other
                  messages.
                </List.Item>
                <List.Item>
                  <b>When:</b> during your office's normal business hours.
                  Messages sent outside those hours are held until they reopen.
                </List.Item>
                <List.Item>
                  <b>How to stop:</b> reply STOP to any message, or turn text
                  messages off any time from your account profile.
                </List.Item>
              </List>
              <Text size="xs" c="dimmed">
                By tapping <b>Accept &amp; Verify Phone</b>, you agree to
                receive timesheet workflow text messages from Firetrol at the
                number you provide. Msg frequency varies. Msg &amp; data rates
                may apply. Reply HELP for help, STOP to unsubscribe. Phone
                numbers are not shared with third parties.
              </Text>
              <Group gap="xs">
                <Anchor
                  href="/legal/sms-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                >
                  SMS Terms
                </Anchor>
                <Text size="xs" c="dimmed">
                  ·
                </Text>
                <Anchor
                  href="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                >
                  Privacy Policy
                </Anchor>
              </Group>
              <BasicButton onClick={() => setStep('verify')}>
                Accept &amp; Verify Phone
              </BasicButton>
              <BasicButton
                variant="subtle"
                color="dark"
                onClick={handleDecline}
                loading={declineMutation.isPending}
              >
                Not Now
              </BasicButton>
            </Stack>
          ) : (
            <Stack>
              <Title order={4}>Verify your phone number</Title>
              <PhoneVerificationStepper
                onRequestCode={async (phone) => {
                  await requestCodeMutation.mutateAsync(phone);
                }}
                onVerifyCode={async (code) => {
                  await verifyMutation.mutateAsync(code);
                }}
                onVerified={handleVerified}
                onCancel={() => setStep('consent')}
              />
            </Stack>
          )}
        </Card>
      </Center>
    </Box>
  );
};
